import { useState } from "react";
import { recomputeNormalizationWer } from "../../services/api";

const DICT_FILTERS = ["safe_child", "hindi_child", "hindi_doro"];
const ALL_FILTERS = ["punctuation", ...DICT_FILTERS];

function formatPct(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `${(v * 100).toFixed(2)}%`;
}

function formatDelta(latest, baseline) {
  if (latest == null || baseline == null) return "";
  const d = latest - baseline;
  const sign = d > 0 ? "+" : "";
  const color = d > 0 ? "#dc2626" : d < 0 ? "#16a34a" : "var(--muted)";
  return (
    <span style={{ color, fontSize: 11, marginLeft: 6 }}>
      ({sign}{(d * 100).toFixed(2)}%)
    </span>
  );
}

export default function NormalizationReplayCard({ runId }) {
  const [toggles, setToggles] = useState({
    punctuation: true,
    safe_child: true,
    hindi_child: true,
    hindi_doro: true,
  });
  const [uploads, setUploads] = useState({}); // { safe_child: File, ... }
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleToggle = (name) => () => {
    setToggles((t) => ({ ...t, [name]: !t[name] }));
  };

  const handleUpload = (name) => (e) => {
    const file = e.target.files?.[0] || null;
    setUploads((u) => {
      const next = { ...u };
      if (file) next[name] = file;
      else delete next[name];
      return next;
    });
  };

  const handleClearUpload = (name) => () => {
    setUploads((u) => {
      const next = { ...u };
      delete next[name];
      return next;
    });
  };

  const handleRecompute = async () => {
    if (!runId || runId === "latest") {
      setError("Pick a concrete run from the top dropdown first.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await recomputeNormalizationWer({
        runId,
        filters: toggles,
        dictOverrides: uploads,
      });
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Recompute failed.");
    } finally {
      setLoading(false);
    }
  };

  const latestColLabel = (() => {
    const onCount = ALL_FILTERS.filter((f) => toggles[f]).length;
    const overrideCount = Object.keys(uploads).length;
    if (overrideCount > 0) return `Latest (${onCount}/4 on · ${overrideCount} override)`;
    return `Latest (${onCount}/4 on)`;
  })();

  return (
    <div className="eval-section-card" style={{ marginBottom: 20 }}>
      <h3 style={{ marginTop: 0 }}>Normalization replay</h3>
      <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 0 }}>
        Recompute corpus WER for the selected run with a different normalization chain.
        Toggle filters below and optionally upload a replacement JSON for any dict-based
        filter to see how the WER would change if that dict were used instead.
      </p>

      <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>
        Run: <code>{runId || "—"}</code>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 220px", gap: 8, alignItems: "center", marginBottom: 12 }}>
        {/* Punctuation row — no upload option */}
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={toggles.punctuation} onChange={handleToggle("punctuation")} />
          punctuation
        </label>
        <div style={{ fontSize: 12, color: "var(--muted)" }}>
          Regex-based: strips ASCII punctuation + Devanagari danda.
        </div>
        <div />

        {/* Dict filter rows */}
        {DICT_FILTERS.map((name) => (
          <div key={name} style={{ display: "contents" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={toggles[name]} onChange={handleToggle(name)} />
              {name}
            </label>
            <div style={{ fontSize: 12, color: "var(--muted)" }}>
              {uploads[name]
                ? `Active dict: ${uploads[name].name} (uploaded, ${uploads[name].size.toLocaleString()} bytes)`
                : "Active dict: backend default"}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <label className="eval-btn secondary" style={{ cursor: "pointer", fontSize: 12 }}>
                Upload JSON
                <input
                  type="file"
                  accept="application/json,.json"
                  style={{ display: "none" }}
                  onChange={handleUpload(name)}
                />
              </label>
              {uploads[name] && (
                <button className="eval-btn secondary" style={{ fontSize: 12 }} onClick={handleClearUpload(name)}>
                  Clear
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <button className="eval-btn" onClick={handleRecompute} disabled={loading || !runId || runId === "latest"}>
        {loading ? "Recomputing…" : "Recompute WER"}
      </button>

      {error && <div className="eval-banner error" style={{ marginTop: 12 }}>Error: {error}</div>}

      {data && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 6 }}>
            {data.n_rows_in_csv?.toLocaleString()} rows in CSV ·
            filters applied: {ALL_FILTERS.filter((f) => data.filters_applied?.[f]).join(", ") || "none"}
            {data.overrides_used?.length > 0 && ` · overrides: ${data.overrides_used.join(", ")}`}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.12)", textAlign: "left" }}>
                <th style={{ padding: "6px 8px" }}>Model</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Raw</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Sarvam norm</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Custom norm</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>{latestColLabel}</th>
              </tr>
            </thead>
            <tbody>
              {(data.models || []).map((m) => (
                <tr key={m.name} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                  <td style={{ padding: "6px 8px", fontFamily: "ui-monospace, monospace" }}>{m.name}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {formatPct(m.raw)}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {formatPct(m.sarvam_norm)}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {formatPct(m.custom_norm)}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {formatPct(m.latest_norm)}
                    {formatDelta(m.latest_norm, m.custom_norm)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 6, fontSize: 11, color: "var(--muted)" }}>
            Δ in parentheses compares <strong>Latest</strong> against <strong>Custom norm</strong> (the run's default chain).
            Green = WER dropped, red = WER rose.
          </div>
        </div>
      )}
    </div>
  );
}
